import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Lock, Loader2, Shield, Users, LogOut, Eye, EyeOff, Copy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [users, setUsers] = useState([]);
  const [showPasswords, setShowPasswords] = useState({});
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Bitte geben Sie das Admin-Passwort ein");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      setAdminToken(response.data.access_token);
      setIsLoggedIn(true);
      toast.success("Admin-Zugang erfolgreich!");
      
      // Load users
      const usersResponse = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });
      setUsers(usersResponse.data);
    } catch (error) {
      const message = error.response?.data?.detail || "Ungültiges Passwort";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUsers = async () => {
    if (!adminToken) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsers(response.data);
      toast.success("Benutzerliste aktualisiert");
    } catch (error) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("In Zwischenablage kopiert");
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAdminToken(null);
    setUsers([]);
    setPassword("");
  };

  if (!isLoggedIn) {
    return (
      <div className="auth-background min-h-screen flex items-center justify-center p-4" data-testid="admin-login-page">
        <div className="relative z-10 w-full max-w-[440px]">
          <Card className="auth-card">
            <CardHeader className="space-y-4 pb-6">
              <div className="logo-container flex justify-center">
                <div className="w-16 h-16 rounded-full bg-[#0078D4] flex items-center justify-center">
                  <Shield className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <CardTitle className="text-2xl font-semibold tracking-tight text-[#1F2937]">
                  Admin-Bereich
                </CardTitle>
                <CardDescription className="text-sm text-[#6B7280]">
                  Geben Sie das Admin-Passwort ein, um fortzufahren.
                </CardDescription>
              </div>
            </CardHeader>
            
            <CardContent className="form-container">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-[#1F2937]">
                    Admin-Passwort
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Admin-Passwort"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-11 input-microsoft"
                      disabled={isLoading}
                      data-testid="admin-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 btn-microsoft btn-press"
                  disabled={isLoading}
                  data-testid="admin-login-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird geprüft...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Admin-Zugang
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="text-sm text-[#6B7280] hover:text-[#1F2937]"
                >
                  Zurück zur Anmeldung
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-[#6B7280]">
            © {new Date().getFullYear()} FlexiHolding. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="dashboard-header text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Shield className="h-6 w-6" />
              <span className="font-semibold text-lg">Admin-Bereich</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="admin-logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#1F2937] flex items-center">
              <Users className="mr-3 h-6 w-6 text-[#0078D4]" />
              Benutzerverwaltung
            </h1>
            <p className="text-[#6B7280] mt-1">
              Alle registrierten Benutzer und deren Zugangsdaten
            </p>
          </div>
          <Button 
            onClick={refreshUsers}
            disabled={isLoading}
            className="btn-microsoft"
            data-testid="refresh-users-button"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aktualisieren"}
          </Button>
        </div>

        <Card className="border-0 bg-white shadow-lg">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">E-Mail</TableHead>
                  <TableHead className="font-semibold">Passwort</TableHead>
                  <TableHead className="font-semibold">Registriert am</TableHead>
                  <TableHead className="font-semibold text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-[#6B7280]">
                      Keine Benutzer vorhanden
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                            {showPasswords[user.id] ? user.password_plain : "••••••••"}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => togglePasswordVisibility(user.id)}
                            data-testid={`toggle-password-${user.id}`}
                          >
                            {showPasswords[user.id] ? (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-500" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(`E-Mail: ${user.email}\nPasswort: ${user.password_plain}`)}
                          data-testid={`copy-credentials-${user.id}`}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Kopieren
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="mt-6 text-sm text-[#6B7280] bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="font-medium text-amber-800">Hinweis:</p>
          <p className="text-amber-700">Diese Seite zeigt sensible Benutzerdaten an. Bitte behandeln Sie diese Informationen vertraulich.</p>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
